import { queryOptions, useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';

import { REFRESH_TOKEN_THRESHOLD } from '@/constants';
import { useMutationNext } from '@/query';
import type { MutationOptionsNext } from '@/query/types';
import { loginServerFn } from '@/server/auth/login';
import { refreshServerFn } from '@/server/auth/refresh';
import { callbackServerFn } from '@/server/callback';
import type {
  TLAICompletePasswordUpdateRequest,
  TLAILoginRequest,
  TLAILoginResponse,
  TLAINextApiLogoutResponse,
  TLAIPasswordUpdateRequiredResponse,
  TLAIRefreshTokenResponse,
  TLAISSOCallbackRequest,
  TLAISSOConfig,
  TLAISSOLoginRequest,
  TLAISSOLoginResponse,
  TLAISuccessResponse,
  TLAIUpdatePasswordRequest,
  TLAIUserProfile,
} from '@/types/api';
import { getApiClient, proxylessApiClient } from '@/utils/api-client';
import type { QueryOptions } from '@/utils/types';

export function useLogin(
  options?: MutationOptionsNext<TLAIPasswordUpdateRequiredResponse | undefined, Error, TLAILoginRequest>,
) {
  const login = useServerFn(loginServerFn);
  return useMutationNext({
    mutationFn: async (input: TLAILoginRequest) => login({ data: input }),
    ...options,
  });
}

export function useLogout(options?: MutationOptionsNext<TLAINextApiLogoutResponse, Error>) {
  return useMutationNext({
    mutationFn: async () => proxylessApiClient().post<TLAINextApiLogoutResponse>('/logout'),
    ...options,
  });
}

export function useClearCookies(options?: MutationOptionsNext) {
  return useMutationNext({
    mutationFn: async () => proxylessApiClient().post('/clearcookies'),
    ...options,
  });
}

const SSO_CONFIG_STALE_TIME = 5 * 60 * 1000;

/* eslint-disable @tanstack/query/exhaustive-deps */
export function useRefreshToken() {
  const redirectTo = useLocation({ select: (location) => location.href });
  const refresh = useServerFn(refreshServerFn);

  return useQuery({
    queryKey: ['auth', 'refresh'],
    queryFn: async () => {
      const response = await refresh({ data: { redirectTo } });
      return (await response.json()) as TLAIRefreshTokenResponse;
    },
    refetchInterval(query) {
      const expiresIn = query.state.data?.expires_in;

      if (!expiresIn) return false;

      return Math.max(0, expiresIn - REFRESH_TOKEN_THRESHOLD) * 1_000;
    },
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
}
/* eslint-enable @tanstack/query/exhaustive-deps */

export const ssoConfigQueryOptions = (options?: QueryOptions<TLAISSOConfig>) =>
  queryOptions({
    queryKey: ['sso', 'config'],
    queryFn: async () =>
      (await getApiClient({ server: { authenticated: false } })).get<TLAISSOConfig>('/auth/sso/config'),
    staleTime: SSO_CONFIG_STALE_TIME,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });

export function useSSOConfig(options?: QueryOptions<TLAISSOConfig>) {
  return useQuery(ssoConfigQueryOptions(options));
}

export function useSSOLogin(options?: MutationOptionsNext<TLAISSOLoginResponse, Error, TLAISSOLoginRequest>) {
  return useMutationNext({
    mutationFn: async (input: TLAISSOLoginRequest) =>
      (await getApiClient()).post<TLAISSOLoginResponse>('/auth/sso/login', input),
    ...options,
  });
}

export function useSSOCallback(options?: MutationOptionsNext<TLAILoginResponse, Error, TLAISSOCallbackRequest>) {
  const callback = useServerFn(callbackServerFn);
  return useMutationNext({
    mutationFn: async (input: TLAISSOCallbackRequest) => {
      const response = await callback({ data: input });
      return (await response.json()) as TLAILoginResponse;
    },
    ...options,
  });
}

export function useUpdatePassword(
  options?: MutationOptionsNext<TLAISuccessResponse, Error, TLAIUpdatePasswordRequest>,
) {
  return useMutationNext({
    mutationFn: async (input: TLAIUpdatePasswordRequest) =>
      (await getApiClient()).put<TLAISuccessResponse>('/auth/password', input),
    ...options,
  });
}

export function useCompleteRequiredPasswordUpdate(
  options?: MutationOptionsNext<TLAISuccessResponse, Error, TLAICompletePasswordUpdateRequest>,
) {
  return useMutationNext({
    mutationFn: async (input: TLAICompletePasswordUpdateRequest) =>
      (await getApiClient()).post<TLAISuccessResponse>('/auth/password/required', input),
    ...options,
  });
}

export const userProfileQueryOptions = (options?: QueryOptions<TLAIUserProfile>) =>
  queryOptions({
    queryKey: ['auth', 'profile'],
    queryFn: async () => (await getApiClient()).get<TLAIUserProfile>('/auth/profile'),
    refetchOnWindowFocus: false,
    ...options,
  });

export function useUserProfile(options?: QueryOptions<TLAIUserProfile>) {
  return useQuery(userProfileQueryOptions(options));
}
